import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button } from '../styles/GlobalStyles';
import { X } from 'lucide-react';
import ContactForm from './ContactForm';

const FranchiseSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 70%, rgba(255, 107, 107, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 70% 30%, rgba(78, 205, 196, 0.05) 0%, transparent 50%);
  }
`;

const FranchiseCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 25px;
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
`;

// Animated keyframes for text effects
const shimmerEffect = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    text-shadow: 
      0 0 5px rgba(255, 215, 0, 0.5),
      0 0 10px rgba(255, 215, 0, 0.3),
      0 0 15px rgba(255, 215, 0, 0.2);
  }
  50% {
    text-shadow: 
      0 0 10px rgba(255, 215, 0, 0.8),
      0 0 20px rgba(255, 215, 0, 0.6),
      0 0 30px rgba(255, 215, 0, 0.4);
  }
`;

const scaleInAnimation = keyframes`
  0% {
    transform: scale(0.8) translateY(20px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
`;

// Typography hierarchy container
const FranchiseTitleContainer = styled.div`
  margin-bottom: 2rem;
  position: relative;
  animation: ${scaleInAnimation} 1s ease-out;
`;

// Crown icons with floating animation
const CrownIcon = styled.div<{ delay?: string }>`
  display: inline-block;
  font-size: 2rem;
  margin: 0 1rem;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  filter: drop-shadow(0 4px 8px rgba(255, 215, 0, 0.3));
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0 0.5rem;
  }
`;

// Main title with improved hierarchy
const FranchiseTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.1;
  margin: 0;
  position: relative;
  
  .join-text {
    display: block;
    font-size: clamp(1rem, 2.5vw, 1.5rem);
    font-weight: 600;
    margin-bottom: 0.5rem;
    background: linear-gradient(
      45deg,
      #FFD700,
      #FFA500,
      #FFD700,
      #FFA500
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmerEffect} 3s ease-in-out infinite;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  
  .kingdom-text {
    display: block;
    font-size: clamp(1.8rem, 4vw, 3rem);
    background: linear-gradient(
      135deg,
      #8B0000 0%,
      #FFD700 25%,
      #FFC300 50%,
      #DAA520 75%,
      #8B0000 100%
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmerEffect} 4s ease-in-out infinite reverse;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 1rem;
  }
`;

// Massive Royale Franchise text
const RoyaleFranchiseTitle = styled.h2`
  font-size: clamp(2.5rem, 8vw, 6rem);
  font-weight: 900;
  line-height: 0.9;
  margin: 3rem 0 4rem 0;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  background: linear-gradient(
    135deg,
    #FFD700 0%,
    #FFC300 15%,
    #8B0000 30%,
    #FFD700 45%,
    #DAA520 60%,
    #F4D03F 75%,
    #FFD700 90%,
    #FFC300 100%
  );
  background-size: 400% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmerEffect} 5s ease-in-out infinite;
  
  text-shadow: 
    0 0 10px rgba(255, 215, 0, 0.3),
    0 0 20px rgba(255, 107, 107, 0.2),
    0 0 30px rgba(139, 69, 19, 0.1);
  
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: linear-gradient(
      45deg,
      rgba(255, 215, 0, 0.1),
      rgba(255, 107, 107, 0.1),
      rgba(139, 69, 19, 0.1)
    );
    border-radius: 20px;
    z-index: -1;
    filter: blur(20px);
    animation: ${glowPulse} 3s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    height: 4px;
    background: linear-gradient(
      90deg,
      transparent,
      #FFD700,
      #8B0000,
      #FFD700,
      transparent
    );
    border-radius: 2px;
    animation: ${shimmerEffect} 3s ease-in-out infinite;
  }
`;

// Removed unused styled component FranchiseDescription

const FranchiseFeatures = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

const FeatureCard = styled.div<{ bgGradient: string; characterImage?: string }>`
  background: ${props => props.bgGradient};
  background-image: ${props => props.characterImage ? `url('/images/image-branding/${props.characterImage}')` : 'none'};
  background-size: cover;
  background-position: center;
  background-blend-mode: overlay;
  border-radius: 20px;
  padding: 2rem 1.5rem;
  color: white;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.characterImage ? 
      'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)' : 
      'rgba(255, 255, 255, 0.1)'
    };
    opacity: ${props => props.characterImage ? 1 : 0};
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: ${props => props.characterImage ? 0.8 : 1};
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
  opacity: 0.9;
  flex-grow: 1;
`;

const FeatureBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-block;
`;

const SignatureBurgersShowcase = styled.div`
  background: linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%);
  border-radius: 25px;
  padding: 3rem 2rem;
  margin: 4rem 0 3rem 0;
  border: 2px solid rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
`;

const ShowcaseTitle = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFD700, #FFC300);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.5));
`;

const ShowcaseSubtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  margin-bottom: 3rem;
  font-weight: 500;
`;

const CharactersLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 2rem;
  align-items: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const SideCharacter = styled.div<{ characterImage: string }>`
  background-image: url('/images/image-branding/${props => props.characterImage}');
  background-size: cover;
  background-position: center;
  height: 250px;
  border-radius: 20px;
  border: 3px solid rgba(255, 215, 0, 0.4);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    border-color: rgba(255, 215, 0, 0.6);
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const CenterShowcase = styled.div`
  background-image: url('/images/image-branding/franchinse.burger.png');
  background-size: cover;
  background-position: center;
  height: 320px;
  border-radius: 25px;
  border: 4px solid rgba(255, 215, 0, 0.5);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(255, 215, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.4) 0%,
      rgba(139, 0, 0, 0.3) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
  }
  
  @media (max-width: 768px) {
    height: 280px;
    margin: 0 -1rem;
  }
`;

const CenterOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
  padding: 2.5rem 1.5rem 1.5rem 1.5rem;
  z-index: 2;
`;

const OverlayTitle = styled.h3`
  color: white;
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  background: linear-gradient(135deg, #FFD700, #FFC300);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.8));
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const OverlayDescription = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  margin: 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  line-height: 1.4;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CrowdSuccessShowcase = styled.div`
  background: linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%);
  border-radius: 25px;
  padding: 3rem 2rem;
  margin: 4rem 0 3rem 0;
  border: 2px solid rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
`;

const CrowdShowcase = styled.div`
  background-image: url('/images/image-branding/franhinse.royal.jpg');
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  height: 450px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  margin: 0 auto;
  width: 100%;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 215, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  /* Improved image quality with better filtering */
  filter: contrast(1.1) saturate(1.1) brightness(1.05);
  
  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 
      0 20px 60px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(255, 215, 0, 0.5),
      0 0 30px rgba(255, 215, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    filter: contrast(1.15) saturate(1.15) brightness(1.1);
  }
  
  /* Enhanced overlay for better text readability */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.1) 30%,
      transparent 60%,
      rgba(255, 215, 0, 0.1) 100%
    );
    transition: all 0.4s ease;
  }
  
  &:hover::before {
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.15) 0%,
      rgba(0, 0, 0, 0.05) 30%,
      transparent 60%,
      rgba(255, 215, 0, 0.15) 100%
    );
  }
  
  /* Professional border styling */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    background: linear-gradient(
      45deg,
      rgba(255, 215, 0, 0.4),
      rgba(255, 215, 0, 0.2),
      rgba(255, 215, 0, 0.4)
    );
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    padding: 2px;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    height: 320px;
    border-radius: 16px;
    
    &::after {
      border-radius: 16px;
    }
    
    &:hover {
      transform: translateY(-3px) scale(1.01);
    }
  }
`;

const CrowdOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  width: 90%;
`;

const CrowdTitle = styled.h3`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFD700, #FFC300);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.5));
`;

const CrowdDescription = styled.p`
  font-size: 1.1rem;
  color: white;
  font-weight: 600;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.4;
`;

const CallToActionSection = styled.div`
  background: linear-gradient(135deg, #FFD700 0%, #FFC300 50%, #FFB000 100%);
  border-radius: 20px;
  padding: 3rem 2rem;
  margin-top: 4rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CTADescription = styled.p`
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Button)`
  background: white;
  color: #8B0000;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border-radius: 15px;
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.25);
  }
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
    padding: 1.2rem 2.5rem;
  }
`;

// Investment Section Styles
const InvestmentSection = styled.div`
  margin: 4rem 0;
  text-align: center;
`;

const InvestmentTitle = styled.h3`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 700;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #FFD700, #8B0000);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const InvestmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const InvestmentCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 15px;
  padding: 2rem 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.1);
  }
`;

const InvestmentNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  color: #FFD700;
  margin-bottom: 0.5rem;
`;

const InvestmentIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(255, 215, 0, 0.3));
`;

const InvestmentLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const InvestmentDesc = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
`;

// Support Section Styles
const SupportSection = styled.div`
  margin: 4rem 0;
  text-align: center;
`;

const SupportTitle = styled.h3`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 700;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #FFD700, #8B0000);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const SupportItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    border-color: rgba(255, 215, 0, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const SupportIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const SupportName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const SupportDesc = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

// Modal styles
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 2rem;
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: all 0.3s ease;
`;

const ModalContainer = styled.div`
  background: rgba(15, 15, 35, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 25px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  backdrop-filter: blur(25px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 0 2rem;
`;

const ModalTitle = styled.h3`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #FFD700, #8B0000);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const ModalContent = styled.div`
  padding: 1rem 2rem 2rem 2rem;
`;

const FranchiseDetails: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('Opening franchise modal'); // Debug log
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    console.log('Closing franchise modal'); // Debug log
    setIsModalOpen(false);
  };

  const handleFormSuccess = () => {
    alert('Thank you for your franchise inquiry! We will contact you within 24 hours.');
    closeModal();
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <FranchiseSection id="franchise">
      <FranchiseCard>
        <FranchiseTitleContainer>
          <CrownIcon delay="0s">👑</CrownIcon>
          <FranchiseTitle>
            <span className="join-text">Join the</span>
            <span className="kingdom-text">Kingdom</span>
          </FranchiseTitle>
          <CrownIcon delay="1.5s">👑</CrownIcon>
        </FranchiseTitleContainer>
        
        <RoyaleFranchiseTitle>
          Royale Franchise
        </RoyaleFranchiseTitle>
        

        <SignatureBurgersShowcase>
          <ShowcaseTitle>
            🍔 Signature Burgers
          </ShowcaseTitle>
          <ShowcaseSubtitle>
            Award-winning recipes made famous by Pulp Fiction's most iconic scene
          </ShowcaseSubtitle>
          
          <CharactersLayout>
            <SideCharacter characterImage="brand3.png" />
            <CenterShowcase>
              <CenterOverlay>
                <OverlayTitle>The Royale Collection</OverlayTitle>
                <OverlayDescription>
                  Lines around the block for our signature burgers
                </OverlayDescription>
              </CenterOverlay>
            </CenterShowcase>
            <SideCharacter characterImage="brand4.png" />
          </CharactersLayout>
        </SignatureBurgersShowcase>
        
        <CrowdSuccessShowcase>
          <ShowcaseTitle>
            👑 Proven Success Story
          </ShowcaseTitle>
          <ShowcaseSubtitle>
            This is what franchise success looks like - real customers, real results
          </ShowcaseSubtitle>
          
          <CrowdShowcase>
            <img 
              src="/images/image-branding/DSC05456.jpg" 
              alt="Crowded restaurant showing franchise success"
              style={{
                width: '100%',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '15px',
                border: '3px solid rgba(255, 215, 0, 0.3)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
              }}
            />
          </CrowdShowcase>
        </CrowdSuccessShowcase>

        {/* Investment & Opportunity Section */}
        <InvestmentSection>
          <InvestmentTitle>
            💰 Franchise Opportunity
          </InvestmentTitle>
          <InvestmentGrid>
            <InvestmentCard>
              <InvestmentIcon>🏆</InvestmentIcon>
              <InvestmentLabel>Proven Concept</InvestmentLabel>
              <InvestmentDesc>Join Detroit's #1 burger destination with a track record of success and customer loyalty</InvestmentDesc>
            </InvestmentCard>
            <InvestmentCard>
              <InvestmentIcon>📈</InvestmentIcon>
              <InvestmentLabel>Strong Returns</InvestmentLabel>
              <InvestmentDesc>Competitive investment with excellent return potential in the growing gourmet burger market</InvestmentDesc>
            </InvestmentCard>
            <InvestmentCard>
              <InvestmentIcon>🎯</InvestmentIcon>
              <InvestmentLabel>Market Leadership</InvestmentLabel>
              <InvestmentDesc>Capitalize on our unique Pulp Fiction branding and award-winning recipes that customers love</InvestmentDesc>
            </InvestmentCard>
          </InvestmentGrid>
        </InvestmentSection>

        {/* Support Package Section */}
        <SupportSection>
          <SupportTitle>
            🤝 Complete Support Package
          </SupportTitle>
          <SupportGrid>
            <SupportItem>
              <SupportIcon>🏗️</SupportIcon>
              <SupportName>Site Selection & Setup</SupportName>
              <SupportDesc>Location analysis, lease negotiation, complete buildout</SupportDesc>
            </SupportItem>
            <SupportItem>
              <SupportIcon>👨‍🍳</SupportIcon>
              <SupportName>Training Program</SupportName>
              <SupportDesc>2-week intensive training for you and your team</SupportDesc>
            </SupportItem>
            <SupportItem>
              <SupportIcon>📱</SupportIcon>
              <SupportName>Tech Stack</SupportName>
              <SupportDesc>POS system, mobile app, online ordering platform</SupportDesc>
            </SupportItem>
            <SupportItem>
              <SupportIcon>📈</SupportIcon>
              <SupportName>Marketing Support</SupportName>
              <SupportDesc>Grand opening campaign, ongoing digital marketing</SupportDesc>
            </SupportItem>
            <SupportItem>
              <SupportIcon>🍔</SupportIcon>
              <SupportName>Supply Chain</SupportName>
              <SupportDesc>Exclusive supplier relationships for premium ingredients</SupportDesc>
            </SupportItem>
            <SupportItem>
              <SupportIcon>📞</SupportIcon>
              <SupportName>Ongoing Support</SupportName>
              <SupportDesc>24/7 operations support and quarterly business reviews</SupportDesc>
            </SupportItem>
          </SupportGrid>
        </SupportSection>
        
        <CallToActionSection>
          <CTATitle>
            👑 Ready to Join the Royale Empire?
          </CTATitle>
          <CTADescription>
            Partner with Detroit's #1 burger destination and leverage our proven success
            formula, complete tech stack, and industry-leading support.
          </CTADescription>
          <CTAButton 
            onClick={(e) => {
              console.log('CTAButton clicked! Opening modal...'); // Debug log
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            onTouchEnd={(e) => {
              console.log('CTAButton touched! Opening modal...'); // Debug log
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            style={{ 
              cursor: 'pointer',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              position: 'relative',
              zIndex: 10,
              pointerEvents: 'auto'
            }}
            type="button"
          >
            Franchise Today
          </CTAButton>
        </CallToActionSection>
      </FranchiseCard>
      
      {/* Franchise Modal */}
      <ModalOverlay isOpen={isModalOpen} onClick={closeModal}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>👑 Franchise Today</ModalTitle>
            <CloseButton onClick={closeModal}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <ModalContent>
            <ContactForm 
              formType="franchise"
              title=""
              subtitle="Ready to own your piece of the Royale with Cheese empire? Our franchise team will contact you within 24 hours."
              onSuccess={handleFormSuccess}
            />
          </ModalContent>
        </ModalContainer>
      </ModalOverlay>
    </FranchiseSection>
  );
};

export default FranchiseDetails; 